import React, { useState, useEffect, useMemo } from "react";
import { useDisclosure } from "@mantine/hooks";
import {
  Group,
  Button,
  Accordion,
  Card,
  Image,
  Flex,
  Loader,
  TextInput,
  ActionIcon,
} from "@mantine/core";
import { ReactComponent as PlusIcon } from "@/assets/icons/plus.svg";
import { ReactComponent as ChevronIcon } from "@/assets/icons/acc-chevron.svg";
import { ReactComponent as XIcon } from "@/assets/icons/x.svg";
import { ReactComponent as SearchIcon } from "@/assets/icons/search.svg";
import AddOrEditModal from "@/components/Admin/Shared/AddOrEditModal/AddOrEditModal";
import { IssueApi } from "@/api/issue.api";
import { Issue, IssueByYear } from "@/api/types";
import dayjs from "dayjs";
import "./Magazines.css";

const Magazines: React.FC = () => {
  const [opened, { open, close }] = useDisclosure(false);
  const [groupedIssues, setGroupedIssues] = useState<IssueByYear[]>();
  const [loading, setLoading] = useState(false);
  const [openedYears, setOpenedYears] = useState<string[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const [issueToEdit, setIssueToEdit] = useState<Issue>();

  const formatMonth = (month: string) => {
    let formatted = dayjs(month).locale("hr").format("MMMM");
    formatted = formatted.charAt(0).toUpperCase() + formatted.slice(1);
    return formatted;
  };

  const fetchIssues = async () => {
    setLoading(true);
    const issues = await IssueApi.getIssuesByYear();
    if (issues) setGroupedIssues(issues);
    setLoading(false);
  };

  const filteredIssues = useMemo(() => {
    if (!groupedIssues) return [];

    const search = searchValue.toLowerCase().trim();

    return groupedIssues
      .map((group) => {
        const matchingIssues = group.issues.filter((issue) =>
          issue.title.toLowerCase().includes(search)
        );
        return {
          ...group,
          issues: matchingIssues,
        };
      })
      .filter((group) => group.issues.length);
  }, [groupedIssues, searchValue]);

  useEffect(() => {
    if (filteredIssues.length && !searchValue)
      setOpenedYears([filteredIssues[0].year]);
  }, [filteredIssues]);

  useEffect(() => {
    fetchIssues();
  }, []);

  const closeModal = () => {
    fetchIssues();
    close();
    setIssueToEdit(undefined);
  };

  const openEditModal = (issue: Issue) => {
    setIssueToEdit(issue);
    open();
  };

  return (
    <div className="px-[5%]">
      <Group
        className="py-[1.5rem] flex min-h-[6rem] !items-center"
        justify="space-between"
      >
        <div className="text-[#0b1a29] text-2xl font-bold">Revije</div>
        <Group>
          <TextInput
            value={searchValue}
            placeholder="Pretraži revije"
            leftSection={<SearchIcon />}
            className="search-input"
            rightSection={
              searchValue && (
                <ActionIcon
                  variant="subtle"
                  aria-label="clear"
                  size="sm"
                  radius="xl"
                  onClick={() => setSearchValue("")}
                >
                  <XIcon width={10} />
                </ActionIcon>
              )
            }
            onChange={(event) => {
              setSearchValue(event.currentTarget.value);
              setOpenedYears(
                event.currentTarget.value.length
                  ? filteredIssues.map((issue) => issue.year)
                  : [filteredIssues[0].year]
              );
            }}
          />
          <Button
            variant="filled"
            color="#004a99"
            radius="xl"
            leftSection={<PlusIcon />}
            size="md"
            className="!h-[3rem]"
            onClick={open}
          >
            Dodaj reviju
          </Button>
        </Group>
      </Group>
      {filteredIssues?.length ? (
        <div className="flex flex-col gap-6 pb-[1.5rem]">
          <Accordion
            variant="separated"
            radius="md"
            defaultValue={[filteredIssues[0].year]}
            value={openedYears}
            multiple
            chevron={<ChevronIcon className="rotate-180" />}
            styles={{
              label: {
                fontSize: "1.125rem",
                lineHeight: "1.75rem",
                fontWeight: "bold",
                color: "#0b1a29",
              },
              content: { padding: 0 },
            }}
            onChange={setOpenedYears}
          >
            {filteredIssues.map((item) => (
              <Accordion.Item
                className="!border-0"
                key={item.year}
                value={item.year}
              >
                <Accordion.Control className="accordion-control">
                  {item.year}.
                </Accordion.Control>
                <Accordion.Panel className="!bg-[white]">
                  <Flex gap={50} wrap="wrap" className="issue-container">
                    {item.issues.map((issue) => {
                      return (
                        <Card
                          key={issue.id}
                          className="issue-card"
                          onClick={() => openEditModal(issue)}
                        >
                          <Card.Section inheritPadding className="pt-4 pb-2">
                            <Image
                              src={issue.cover.url}
                              className="!bg-cover !rounded issue-image"
                            />
                          </Card.Section>
                          <Card.Section
                            inheritPadding
                            className="pb-4 !flex !flex-col gap-1"
                          >
                            <div className="text-[#0b1a29] text-base font-bold text-ellipsis">
                              {issue.title}
                            </div>
                            <div className="text-[#515151] text-sm font-medium">
                              {formatMonth(issue.month)}, {issue.year}
                            </div>
                            <div className="text-[#004a99] text-sm font-bold">
                              {issue.price} €
                            </div>
                          </Card.Section>
                        </Card>
                      );
                    })}
                  </Flex>
                </Accordion.Panel>
              </Accordion.Item>
            ))}
          </Accordion>
        </div>
      ) : loading ? (
        <div className="flex items-center justify-center mt-[5rem]">
          <Loader color="blue" />
        </div>
      ) : (
        <div className="flex mt-4">Trenutno nema revija.</div>
      )}
      <AddOrEditModal
        type="issue"
        initialData={issueToEdit}
        opened={opened}
        onClose={closeModal}
      />
    </div>
  );
};

export default Magazines;
